import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DataApiResponseLegacy } from '@digilize/shared/definitions/src/lib/interfaces/DataApiResponseLegacy';
import {
  GetSubscriptionInstancesQuery,
  SubscriptionBuyerTransferModel,
  SubscriptionBuyerTransferPreviewModel,
  SubscriptionBuyerTransferResultModel,
  SubscriptionCardAddModel,
  SubscriptionCardModel,
  SubscriptionCardValidateModel,
  SubscriptionCardValidationResult,
  SubscriptionHolderDetailsModel,
  SubscriptionHolderDetailsUpdateModel,
  SubscriptionImportBatchModel,
  SubscriptionImportFileOptions,
  SubscriptionImportFileResultModel,
  SubscriptionInstanceBillingPeriodModel,
  SubscriptionInstanceCustomerDetailsModel,
  SubscriptionInstanceEndModel,
  SubscriptionInstanceEndOptionsModel,
  SubscriptionInstanceEndResultModel,
  SubscriptionInstanceImportResultModel,
  SubscriptionInstanceImportRow,
  SubscriptionInstanceListModel,
  SubscriptionInstanceModel,
  SubscriptionInstanceNoShowModel,
  SubscriptionInstanceReinstateModel,
  SubscriptionInstanceReinstateResultModel,
  SubscriptionInstanceStatusModel,
  SubscriptionInstanceStatusTransitionsModel,
  SubscriptionInstanceUserDetailsModel,
  SubscriptionReservationListModel,
} from '@digilize/shared/definitions/src/lib/models';
import { ENV_CONFIG } from '@digilize/shared/utils/tokens';
import { EnvConfig } from '@shared/definitions';

@Injectable({
  providedIn: 'root',
})
export class SubscriptionInstancesApiService {
  private readonly BASE_URL = '/api/v1.0/subscription-instances';

  constructor(private http: HttpClient, @Inject(ENV_CONFIG) private envConfig: EnvConfig) {}

  /* Every list here serialises the DataTables envelope in camelCase (`recordsFiltered`), not the
     snake_case swagger documents for it, hence DataApiResponseLegacy. */
  getInstances = (params?: GetSubscriptionInstancesQuery) =>
    this.http.get<DataApiResponseLegacy<SubscriptionInstanceListModel[]>>(this.BASE_URL, {
      params: params as Record<string, any>,
    });

  getInstance = (id: string) => this.http.get<SubscriptionInstanceModel>(`${this.BASE_URL}/${id}`);

  // Warn-then-confirm: a violating end_date returns 200 with `is_ended: false` + warning
  // payload; resubmit with `confirm: true` (and optionally `include_setup_fee`) to proceed.
  endInstance = (id: string, body: SubscriptionInstanceEndModel) =>
    this.http.put<SubscriptionInstanceEndResultModel>(`${this.BASE_URL}/${id}/end`, body);

  /* What the end dialog may offer, before anything is submitted: the earliest allowed end date and
     the billing-period boundaries worth recommending. Changes nothing, and is refused exactly where
     endInstance is. */
  getEndOptions = (id: string) =>
    this.http.get<SubscriptionInstanceEndOptionsModel>(`${this.BASE_URL}/${id}/end-options`);

  /* Everything the change-status dialog may offer, and why a target is refused. The transition
     table lives server-side only; setInstanceStatus accepts exactly what this advertises, so a
     target listed here as `allowed` is one the PUT will take. */
  getStatusTransitions = (id: string) =>
    this.http.get<SubscriptionInstanceStatusTransitionsModel>(`${this.BASE_URL}/${id}/status-transitions`);

  setInstanceStatus = (id: string, body: SubscriptionInstanceStatusModel) =>
    this.http.put<void>(`${this.BASE_URL}/${id}/status`, body);

  /* Un-cancels an instance whose term is still running (PAUL-3357). Its own endpoint rather than a
     status write because it moves money: the invoices cancelled with the subscription come back
     open and a direct debit can be scheduled. Warn-then-confirm like endInstance, but the preview
     is not an exception path — `confirm: false` writes nothing and returns what confirming would
     do, and that summary is what support pastes into their mail to the holder, since the system
     sends none. Offered only where getStatusTransitions marks a transition `action: 'reinstate'`. */
  reinstateInstance = (id: string, body: SubscriptionInstanceReinstateModel) =>
    this.http.put<SubscriptionInstanceReinstateResultModel>(`${this.BASE_URL}/${id}/reinstate`, body);

  setBillingPeriod = (id: string, body: SubscriptionInstanceBillingPeriodModel) =>
    this.http.put<void>(`${this.BASE_URL}/${id}/billing-period`, body);

  resetNoShows = (id: string) => this.http.put<void>(`${this.BASE_URL}/${id}/reset-no-shows`, {});

  // Links or unlinks the account only; the backend model is `{ user_id }`.
  setCustomerDetails = (id: string, body: SubscriptionInstanceCustomerDetailsModel) =>
    this.http.put<void>(`${this.BASE_URL}/${id}/customer-details`, body);

  /* What moving the paying role to `userId` would do (PAUL-3649): both accounts, their direct
     debits, and the direct-debit options the pair allows. Changes nothing; an unacceptable account
     is refused here exactly as transferBuyer refuses it. */
  getBuyerTransferPreview = (id: string, userId: string) =>
    this.http.get<SubscriptionBuyerTransferPreviewModel>(`${this.BASE_URL}/${id}/buyer-transfer`, {
      params: { user_id: userId },
    });

  transferBuyer = (id: string, body: SubscriptionBuyerTransferModel) =>
    this.http.put<SubscriptionBuyerTransferResultModel>(`${this.BASE_URL}/${id}/buyer`, body);

  /* The card's own holder: the personalization stored on the instance, plus the product's
     per-field requirements. Moderator+ of the instance's company only; a POS-role staff user
     gets 403 on both verbs. */
  getUserDetails = (id: string) =>
    this.http.get<SubscriptionInstanceUserDetailsModel>(`${this.BASE_URL}/${id}/user-details`);

  // Full replace: an omitted field is cleared, which is the only way to remove an optional
  // birthday or photo. The response carries the instance's status, which flips Paused -> Active
  // when this save completes the last missing required detail.
  setUserDetails = (id: string, body: SubscriptionHolderDetailsUpdateModel) =>
    this.http.put<SubscriptionHolderDetailsModel>(`${this.BASE_URL}/${id}/user-details`, body);

  getCards = (id: string) => this.http.get<SubscriptionCardModel[]>(`${this.BASE_URL}/${id}/cards`);

  addCard = (id: string, body: SubscriptionCardAddModel) =>
    this.http.post<SubscriptionCardModel>(`${this.BASE_URL}/${id}/cards`, body);

  assignCard = (id: string, body: SubscriptionCardAddModel) =>
    this.http.post<SubscriptionCardModel>(`${this.BASE_URL}/${id}/assign-card`, body);

  blockCard = (cardId: string) => this.http.put<void>(`${this.BASE_URL}/cards/${cardId}/block`, {});

  unblockCard = (cardId: string) => this.http.put<void>(`${this.BASE_URL}/cards/${cardId}/unblock`, {});

  unblockUserConnect = (userId: string) => this.http.put<void>(`${this.BASE_URL}/cards/unblock-user/${userId}`, {});

  getReservations = (id: string, params?: Record<string, unknown>) =>
    this.http.get<DataApiResponseLegacy<SubscriptionReservationListModel[]>>(`${this.BASE_URL}/${id}/reservations`, {
      params: params as Record<string, any>,
    });

  /* Returns a BARE ARRAY, not the `{ data: [...] }` envelope every list above uses: the action is
     `return Json(items)` over a `List<>`, so there is nothing to unwrap. Unpaged and newest-first.
     Note this is history, not the counter: reset-no-shows zeroes `no_show_count` without deleting
     rows, so a zero count can still return a non-empty list. */
  getNoShows = (id: string) => this.http.get<SubscriptionInstanceNoShowModel[]>(`${this.BASE_URL}/${id}/no-shows`);

  validateCard = (body: SubscriptionCardValidateModel) =>
    this.http.post<SubscriptionCardValidationResult>(`${this.BASE_URL}/cards/validate`, body);

  /* The card's code as a QR PNG — the digital pass in the portal, the printed one in the dashboard.
     It encodes the bare card code, the same string validateCard and the cash register accept, so
     scanning the screen behaves exactly like scanning the plastic.
     Fetched as a blob because the portal's pass reads the bytes (it inlines them as a data URL). To
     just show the image, bind cardQrPngUrl into an <img src> instead - the endpoint is anonymous on
     purpose, so nothing needs the bearer token an <img> could not send anyway.
     `size` is an upper bound the encoder floors, so ask for more than is rendered and size the box
     in CSS. Blocked cards still render — the portal greys them out rather than hiding the image. */
  getCardQrPng = (cardId: string, size = 240) =>
    this.http.get(`${this.BASE_URL}/cards/${cardId}/qr.png`, { params: { size }, responseType: 'blob' });

  /* The same image as a URL rather than a request, for hover previews that bind it straight into an
     <img src>. Absolute, and the only URL here that is: a native image load never passes through
     ApiInterceptor, so on a deployed dashboard a relative /api path would resolve against the Angular
     host, whose SPA fallback answers index.html - a 200 of text/html that renders as an empty box.
     Same shape and same reason as POSApiService.seatQrCodePngUrl; the /api strip mirrors
     ApiInterceptor.setProxy so BASE_URL stays the single source of truth. */
  cardQrPngUrl = (cardId: string, size = 240) =>
    `${this.envConfig.api}${this.BASE_URL.replace('/api', '')}/cards/${cardId}/qr.png?size=${size}`;

  // ── Migration import (platform Admin only — 403 otherwise) ────────────────

  // Per-row failures are reported inside the 200 body (`results[]`), not as HTTP errors.
  importInstances = (rows: SubscriptionInstanceImportRow[]) =>
    this.http.post<SubscriptionInstanceImportResultModel>(`${this.BASE_URL}/import`, rows);

  importInstancesFile = (file: File, options?: SubscriptionImportFileOptions) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.file_config_has_header != null) {
      formData.append('file_config_has_header', String(options.file_config_has_header));
    }
    if (options?.file_config_csv_encoding) {
      formData.append('file_config_csv_encoding', options.file_config_csv_encoding);
    }
    if (options?.file_config_csv_separator) {
      formData.append('file_config_csv_separator', options.file_config_csv_separator);
    }
    return this.http.post<SubscriptionImportFileResultModel>(`${this.BASE_URL}/import/file`, formData);
  };

  getImportBatches = (params?: { draw?: number; start?: number; length?: number }) =>
    this.http.get<DataApiResponseLegacy<SubscriptionImportBatchModel[]>>(`${this.BASE_URL}/import/batches`, {
      params: params as Record<string, any>,
    });
}
